import React from 'react';
import { 
  Box, 
  Button, 
  Paper, 
  Typography, 
  Container,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContactSupportOutlined from '@mui/icons-material/ContactSupportOutlined';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';

const BRAND_PURPLE = '#4338ca';

const IconExporter = () => {
  const icons = [
    { name: 'Resources', Icon: ShareIcon },
    { name: 'Support', Icon: ContactSupportOutlined },
    { name: 'AI Insights', Icon: AssistantOutlinedIcon },
    { name: 'Insights & Actions', Icon: TipsAndUpdatesRoundedIcon },
  ];

  const exportIconAlternative = async (name, iconRef) => {
    if (!iconRef) return;

    // Get the SVG element from the icon
    const svgElement = iconRef.querySelector('svg');
    if (!svgElement) return;

    // Clone the SVG to avoid modifying the original
    const clonedSvg = svgElement.cloneNode(true);
    
    // Set size to large dimensions for high quality
    const size = 1024; // Increased from 512 for even better quality
    clonedSvg.setAttribute('width', size);
    clonedSvg.setAttribute('height', size);
    
    // Set viewBox to ensure proper scaling from the 24x24 Material UI default
    clonedSvg.setAttribute('viewBox', '0 0 24 24');
    clonedSvg.style.color = BRAND_PURPLE;
    
    // Find all path elements and set fill color
    const paths = clonedSvg.querySelectorAll('path');
    paths.forEach(path => {
      path.setAttribute('fill', BRAND_PURPLE);
      path.setAttribute('stroke', 'none');
    });

    // Also handle any other shape elements
    const circles = clonedSvg.querySelectorAll('circle');
    circles.forEach(circle => {
      circle.setAttribute('fill', BRAND_PURPLE);
      circle.setAttribute('stroke', 'none');
    });

    const rects = clonedSvg.querySelectorAll('rect');
    rects.forEach(rect => {
      rect.setAttribute('fill', BRAND_PURPLE);
      rect.setAttribute('stroke', 'none');
    });

    // Convert SVG to string
    const svgString = new XMLSerializer().serializeToString(clonedSvg);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    
    // Create canvas with high quality settings
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', { alpha: true });
    
    // Enable image smoothing for better quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Create image from SVG
    const img = new Image();
    const url = URL.createObjectURL(svgBlob);
    
    img.onload = () => {
      // Draw icon (no background fill for transparency)
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      
      // Convert to PNG and download
      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        link.download = `${name.toLowerCase().replace(/\s+/g, '-')}-icon.png`;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      }, 'image/png');
    };
    
    img.src = url;
  };

  const exportAllIcons = () => {
    icons.forEach(({ name }, index) => {
      const iconRef = document.getElementById(`icon-${index}`);
      setTimeout(() => {
        exportIconAlternative(name, iconRef);
      }, index * 500); // Stagger downloads
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom color="primary">
          Icon Exporter
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Export Material UI icons as PNG images in brand purple ({BRAND_PURPLE})
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button 
            variant="contained" 
            onClick={exportAllIcons}
            size="large"
          >
            Export All Icons
          </Button>
        </Box>

        <Grid container spacing={3}>
          {icons.map(({ name, Icon }, index) => (
            <Grid item xs={12} sm={6} md={3} key={name}>
              <Card>
                <CardContent>
                  <Box 
                    id={`icon-${index}`}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      mb: 2,
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 2
                    }}
                  >
                    <Icon 
                      sx={{ 
                        fontSize: 64, 
                        color: BRAND_PURPLE 
                      }} 
                    />
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    align="center" 
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {name}
                  </Typography>
                  <Button 
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const iconRef = document.getElementById(`icon-${index}`);
                      exportIconAlternative(name, iconRef);
                    }}
                  >
                    Export PNG
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Note:</strong> Icons will be exported as 1024x1024 high-quality PNG files with a transparent background.
            The icons use the brand purple color: {BRAND_PURPLE}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default IconExporter;
